
import api from './api';

export interface SystemInfo {
    status: string;
    version: string;
    stats: {
        users: number;
        restaurants: number;
        orders: number;
        partners: number;
        expiring_this_month: number;
    };
    timestamp: string;
}

export type ActivityEventType = 'restaurant' | 'user' | 'subscription';

export interface ActivityEvent {
    type: ActivityEventType;
    label: string;
    date: string;
}

export interface Incident {
    id: string;
    type: string;
    severity: 'error' | 'warning' | 'info';
    message: string;
    context: Record<string, unknown>;
    created_at: string;
}

export interface ServerMetrics {
    cpu: {
        usage_percent: number;
        cores: number;
        model: string;
    };
    memory: {
        total_mb: number;
        used_mb: number;
        free_mb: number;
        usage_percent: number;
    };
    uptime_seconds: number;
    platform: string;
    hostname: string;
}

export interface MetricsPoint {
    ts: number;
    cpu: number;
    ram: number;
}

export interface ServiceStatus {
    key: string;
    name: string;
    status: 'up' | 'down';
    latency_ms: number | null;
    checked_at: string;
}

export interface EventLoopPoint {
    ts: number;
    lag_ms: number;
    p99_ms: number;
}

export interface EventLoopStatus {
    current: {
        lag_ms: number;
        p99_ms: number;
        status: 'healthy' | 'degraded' | 'critical';
    };
    history: EventLoopPoint[];
}

export interface RouteLatencyStats {
    route: string;
    count: number;
    p50:   number;
    p95:   number;
    p99:   number;
    max:   number;
}

export const systemService = {
    getSystemInfo: async (): Promise<{ status: string; data: SystemInfo }> => {
        const response = await api.get('/system/info');
        return response.data;
    },
    getRecentActivity: async (): Promise<{ status: string; data: ActivityEvent[] }> => {
        const response = await api.get('/system/recent-activity');
        return response.data;
    },
    getIncidents: async (): Promise<{ status: string; data: Incident[] }> => {
        const response = await api.get('/system/incidents');
        return response.data;
    },
    resolveIncident: async (id: string): Promise<void> => {
        await api.delete(`/system/incidents/${id}`);
    },
    getServerMetrics: async (): Promise<{ status: string; data: ServerMetrics }> => {
        const response = await api.get('/system/metrics');
        return response.data;
    },
    getMetricsHistory: async (): Promise<{ status: string; data: MetricsPoint[] }> => {
        const response = await api.get('/system/metrics/history');
        return response.data;
    },
    getServicesStatus: async (): Promise<{ status: string; data: ServiceStatus[] }> => {
        const response = await api.get('/system/services-status');
        return response.data;
    },
    getEventLoop: async (): Promise<{ status: string; data: EventLoopStatus }> => {
        const response = await api.get('/system/event-loop');
        return response.data;
    },
    getApiLatency: async (): Promise<{ status: string; data: RouteLatencyStats[] }> => {
        const response = await api.get('/system/api-latency');
        return response.data;
    },
    sendPushNotification: async (payload: {
        title: string;
        message: string;
        target: 'all' | 'restaurant';
        restaurantId?: number;
    }): Promise<{ status: string; data: { sent: number; saved: number } }> => {
        const response = await api.post('/system/push-notifications', payload);
        return response.data;
    },
};
